import { useState, useEffect, useCallback, useMemo, useRef, useReducer } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SupplierService } from '@/lib/supplier-service';
import { supabase, getCurrentUser } from '@/lib/supabase';
import {
  Supplier,
  Product,
  Document,
  DashboardStats,
  DashboardTab,
  LoadingStates,
  ErrorStates,
  Notification,
  UseSupplierDashboardReturn
} from '@/polymet/types/supplier';

// Action types for the reducer
type DashboardAction =
  | { type: 'SET_LOADING'; payload: { key: keyof LoadingStates; value: boolean } }
  | { type: 'SET_ERROR'; payload: { key: keyof ErrorStates; value: string | null } }
  | { type: 'CLEAR_ERRORS' }
  | { type: 'SET_SUPPLIER'; payload: Supplier }
  | { type: 'SET_PRODUCTS'; payload: readonly Product[] }
  | { type: 'DELETE_PRODUCT'; payload: string }
  | { type: 'SET_DOCUMENTS'; payload: readonly Document[] }
  | { type: 'DELETE_DOCUMENT'; payload: string }
  | { type: 'SET_STATS'; payload: DashboardStats }
  | { type: 'SET_ACTIVE_TAB'; payload: DashboardTab }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'SET_INITIALIZED'; payload: boolean };

// Initial state
interface DashboardState {
  supplier: Supplier | null;
  products: readonly Product[];
  documents: readonly Document[];
  stats: DashboardStats;
  activeTab: DashboardTab;
  loadingStates: LoadingStates;
  errorStates: ErrorStates;
  notifications: readonly Notification[];
  isInitialized: boolean;
}

const initialState: DashboardState = {
  supplier: null,
  products: [],
  documents: [],
  stats: {
    products: 0,
    inquiries: 0,
    orders: 0,
    revenue: 0
  },
  activeTab: 'overview',
  loadingStates: {
    dashboard: true,
    products: false,
    profile: false,
    documents: false,
    upload: false,
    delete: false,
    save: false,
    verification: false
  },
  errorStates: {},
  notifications: [],
  isInitialized: false
};

// Reducer function with immutable updates
function dashboardReducer(state: DashboardState, action: DashboardAction): DashboardState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loadingStates: { ...state.loadingStates, [action.payload.key]: action.payload.value }
      };
    
    case 'SET_ERROR':
      if (action.payload.value) {
        return {
          ...state,
          errorStates: { ...state.errorStates, [action.payload.key]: action.payload.value }
        };
      } else {
        const { [action.payload.key]: removed, ...rest } = state.errorStates;
        return { ...state, errorStates: rest };
      }
    
    case 'CLEAR_ERRORS':
      return { ...state, errorStates: {} };
    
    case 'SET_SUPPLIER':
      return { ...state, supplier: action.payload };
    
    case 'SET_PRODUCTS':
      return { ...state, products: action.payload };
    
    case 'DELETE_PRODUCT':
      return {
        ...state,
        products: state.products.filter(product => product.id !== action.payload)
      };
    
    case 'SET_DOCUMENTS':
      return { ...state, documents: action.payload };
    
    case 'DELETE_DOCUMENT':
      return {
        ...state,
        documents: state.documents.filter(doc => doc.id !== action.payload)
      };
    
    case 'SET_STATS':
      return { ...state, stats: action.payload };
    
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [...state.notifications, action.payload]
      };
    
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };
    
    case 'CLEAR_NOTIFICATIONS':
      return { ...state, notifications: [] };
    
    case 'SET_INITIALIZED':
      return { ...state, isInitialized: action.payload };
    
    default:
      return state;
  }
}

// Helper function to create unique notification IDs
const createNotificationId = () => `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Helper function to create error with additional context
const createError = (message: string, context?: Record<string, any>) => {
  const error = new Error(message);
  if (context) {
    Object.assign(error, { context });
  }
  return error;
};

export const useSupplierDashboard = (): UseSupplierDashboardReturn => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use reducer for complex state management
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  
  // Memoize SupplierService to prevent recreation
  const supplierService = useMemo(() => new SupplierService(), []);
  
  // Ref to track if component is mounted (prevent state updates after unmount)
  const isMountedRef = useRef(true);
  
  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helper function to safely dispatch actions only when mounted
  const safeDispatch = useCallback((action: DashboardAction) => {
    if (isMountedRef.current) {
      dispatch(action);
    }
  }, []);

  // Helper functions for common operations
  const setLoading = useCallback((key: keyof LoadingStates, value: boolean) => {
    safeDispatch({ type: 'SET_LOADING', payload: { key, value } });
  }, [safeDispatch]);

  const setError = useCallback((key: keyof ErrorStates, value: string | null) => {
    safeDispatch({ type: 'SET_ERROR', payload: { key, value } });
  }, [safeDispatch]);

  const clearErrors = useCallback(() => {
    safeDispatch({ type: 'CLEAR_ERRORS' });
  }, [safeDispatch]);

  // Enhanced notification system
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp'>) => {
    const newNotification: Notification = {
      ...notification,
      id: createNotificationId(),
      timestamp: Date.now(),
      duration: notification.duration ?? 5000,
      dismissible: notification.dismissible ?? true
    };
    
    safeDispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });
    
    // Auto-remove notification after duration
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        if (isMountedRef.current) {
          safeDispatch({ type: 'REMOVE_NOTIFICATION', payload: newNotification.id });
        }
      }, newNotification.duration);
    }
  }, [safeDispatch]);

  const removeNotification = useCallback((notificationId: string) => {
    safeDispatch({ type: 'REMOVE_NOTIFICATION', payload: notificationId });
  }, [safeDispatch]);

  const clearAllNotifications = useCallback(() => {
    safeDispatch({ type: 'CLEAR_NOTIFICATIONS' });
  }, [safeDispatch]);

  // Enhanced tab management with URL sync
  const setActiveTab = useCallback((tab: DashboardTab) => {
    safeDispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
    
    // Update URL params without causing navigation
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams, safeDispatch]);

  // Initialize tab from URL on mount
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') as DashboardTab;
    if (tabFromUrl && ['overview', 'profile', 'brands', 'products', 'documents'].includes(tabFromUrl)) {
      safeDispatch({ type: 'SET_ACTIVE_TAB', payload: tabFromUrl });
    }
  }, [searchParams, safeDispatch]);

  // Enhanced data loading with parallel processing and error recovery
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading('dashboard', true);
      clearErrors();

      const user = await getCurrentUser();
      if (!user) {
        throw createError('No authenticated user found');
      }

      // Get complete supplier information
      const completeInfo = await supplierService.getCompleteSupplierInfo(user.id);
      if (!completeInfo) {
        throw createError('Failed to load supplier information');
      }

      const profile = completeInfo;
      const supplierProfileDetails = completeInfo.supplier_profile;

      // Check authorization
      if (profile.user_type !== 'supplier' && profile.user_type !== 'both') {
        throw createError('User is not authorized as a supplier');
      }

      // Format supplier data with null safety
      const supplierData: Supplier = {
        id: profile.id || '',
        name: profile.full_name || 'Supplier',
        email: profile.email || '',
        phone: profile.phone || '',
        avatar: profile.avatar_url || 'https://github.com/polymet-ai.png',
        company: profile.company_name || '',
        position: 'Supplier',
        location: [profile.city, profile.state].filter(Boolean).join(', '),
        notificationCount: 0,
        verificationStatus: profile.is_verified ? 'verified' : 'pending',
        businessName: supplierProfileDetails?.business_name || profile.company_name || '',
        logo: supplierProfileDetails?.logo_url || profile.avatar_url || '',
        description: supplierProfileDetails?.description || '',
        website: supplierProfileDetails?.website || '',
        gstNumber: supplierProfileDetails?.gst_number || '',
        panNumber: supplierProfileDetails?.pan_number || '',
        address: supplierProfileDetails?.address || ''
      };

      safeDispatch({ type: 'SET_SUPPLIER', payload: supplierData });

      // Load data in parallel with Promise.allSettled for better error handling
      const [productsResult, inquiriesResult, ordersResult, documentsResult] = await Promise.allSettled([
        supplierService.getSupplierProducts(user.id),
        supplierService.getSupplierInquiries(user.id),
        supabase.from('orders').select('*').eq('supplier_id', user.id).order('created_at', { ascending: false }),
        supabase.from('supplier_documents').select('*').eq('supplier_id', user.id).order('created_at', { ascending: false })
      ]);

      // Handle products
      if (productsResult.status === 'fulfilled') {
        safeDispatch({ type: 'SET_PRODUCTS', payload: (productsResult.value || []) as readonly Product[] });
      } else {
        console.error('Failed to load products:', (productsResult as PromiseRejectedResult).reason);
        setError('products', 'Failed to load products');
        addNotification({
          type: 'warning',
          title: 'Products Loading Issue',
          message: 'Some products may not be displayed. Please refresh to try again.'
        });
      }

      // Handle inquiries and orders for stats
      const inquiries = inquiriesResult.status === 'fulfilled' ? inquiriesResult.value || [] : [];
      const orders = ordersResult.status === 'fulfilled' ? ordersResult.value?.data || [] : [];

      // Handle documents
      if (documentsResult.status === 'fulfilled' && documentsResult.value.data) {
        safeDispatch({ type: 'SET_DOCUMENTS', payload: documentsResult.value.data });
      } else {
        console.error('Failed to load documents:', documentsResult.status === 'rejected' ? documentsResult.reason : 'Unknown error');
        setError('documents', 'Failed to load documents');
      }

      // Calculate stats
      const revenue = orders.reduce((sum: number, order: any) => sum + (order.total_amount || 0), 0);
      const stats: DashboardStats = {
        products: productsResult.status === 'fulfilled' ? (productsResult.value?.length || 0) : 0,
        inquiries: inquiries.length,
        orders: orders.length,
        revenue
      };

      safeDispatch({ type: 'SET_STATS', payload: stats });
      safeDispatch({ type: 'SET_INITIALIZED', payload: true });

      // Show success notification for first load
      if (!state.isInitialized) {
        addNotification({
          type: 'success',
          title: 'Dashboard Loaded',
          message: 'Your supplier dashboard has been loaded successfully.',
          duration: 3000
        });
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load dashboard data';
      setError('dashboard', errorMessage);
      
      addNotification({
        type: 'error',
        title: 'Loading Error',
        message: errorMessage,
        duration: 10000
      });
    } finally {
      setLoading('dashboard', false);
    }
  }, [setLoading, clearErrors, setError, safeDispatch, supplierService, addNotification, state.isInitialized]);

  // Enhanced product deletion with optimistic updates
  const deleteProduct = useCallback(async (productId: string) => {
    try {
      setLoading('delete', true);
      setError('delete', null);

      const user = await getCurrentUser();
      if (!user) {
        throw createError('User not authenticated');
      }

      // Optimistic update - remove from UI immediately
      const productToDelete = state.products.find(p => p.id === productId);
      if (productToDelete) {
        safeDispatch({ type: 'DELETE_PRODUCT', payload: productId });
      }

      // Verify ownership
      const { data: productCheck, error: checkError } = await supabase
        .from('products')
        .select('id, supplier_id, name')
        .eq('id', productId)
        .single();

      if (checkError || !productCheck) {
        throw createError('Product not found or access denied');
      }

      if (productCheck.supplier_id !== user.id) {
        throw createError('You do not have permission to delete this product');
      }

      // Perform deletion
      const { error: deleteError } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (deleteError) {
        throw createError(`Failed to delete product: ${deleteError.message}`);
      }

      addNotification({
        type: 'success',
        title: 'Product Deleted',
        message: `Product "${productToDelete?.name || 'Unknown'}" has been deleted successfully.`,
        duration: 5000
      });

    } catch (error) {
      console.error('Error deleting product:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete product';
      
      // Revert optimistic update on error
      await loadDashboardData();
      
      setError('delete', errorMessage);
      addNotification({
        type: 'error',
        title: 'Deletion Failed',
        message: errorMessage,
        duration: 8000
      });
    } finally {
      setLoading('delete', false);
    }
  }, [setLoading, setError, safeDispatch, addNotification, state.products, loadDashboardData]);

  // Enhanced document deletion
  const deleteDocument = useCallback(async (documentId: string) => {
    try {
      setLoading('delete', true);
      setError('delete', null);

      const user = await getCurrentUser();
      if (!user) {
        throw createError('User not authenticated');
      }

      // Optimistic update
      const documentToDelete = state.documents.find(d => d.id === documentId);
      if (documentToDelete) {
        safeDispatch({ type: 'DELETE_DOCUMENT', payload: documentId });
      }

      const { error } = await supabase
        .from('supplier_documents')
        .delete()
        .eq('id', documentId)
        .eq('supplier_id', user.id);

      if (error) {
        throw createError(`Failed to delete document: ${error.message}`);
      }

      addNotification({
        type: 'success',
        title: 'Document Deleted',
        message: 'Document has been deleted successfully.',
        duration: 5000
      });

    } catch (error) {
      console.error('Error deleting document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete document';
      
      // Revert optimistic update on error
      await loadDashboardData();
      
      setError('delete', errorMessage);
      addNotification({
        type: 'error',
        title: 'Deletion Failed',
        message: errorMessage,
        duration: 8000
      });
    } finally {
      setLoading('delete', false);
    }
  }, [setLoading, setError, safeDispatch, addNotification, state.documents, loadDashboardData]);

  // Enhanced document upload with progress tracking
  const uploadDocument = useCallback(async (file: File, documentType: string) => {
    try {
      setLoading('upload', true);
      setError('upload', null);

      const user = await getCurrentUser();
      if (!user) {
        throw createError('User not authenticated');
      }

      // Validate file
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        throw createError('File size must be less than 10MB');
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        throw createError('Only PDF, JPEG, PNG, and GIF files are allowed');
      }

      // Create unique filename
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileExtension = file.name.split('.').pop();
      const fileName = `supplier_documents/${user.id}/${documentType}_${timestamp}_${randomString}.${fileExtension}`;

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, file);

      if (uploadError) {
        throw createError(`Failed to upload file: ${uploadError.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(fileName);

      // Save document record
      const { error: saveError } = await supabase
        .from('supplier_documents')
        .insert({
          supplier_id: user.id,
          document_type: documentType,
          document_url: urlData.publicUrl,
          verified: false
        });

      if (saveError) {
        throw createError(`Failed to save document record: ${saveError.message}`);
      }

      // Refresh documents
      await loadDashboardData();

      addNotification({
        type: 'success',
        title: 'Document Uploaded',
        message: `${documentType} document has been uploaded successfully and is pending verification.`,
        duration: 5000
      });

    } catch (error) {
      console.error('Error uploading document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload document';
      
      setError('upload', errorMessage);
      addNotification({
        type: 'error',
        title: 'Upload Failed',
        message: errorMessage,
        duration: 8000
      });
    } finally {
      setLoading('upload', false);
    }
  }, [setLoading, setError, addNotification, loadDashboardData]);

  // Refresh products data
  const refreshProducts = useCallback(async () => {
    try {
      setLoading('products', true);
      setError('products', null);

      const user = await getCurrentUser();
      if (!user) {
        throw createError('User not authenticated');
      }

      const products = await supplierService.getSupplierProducts(user.id);
      safeDispatch({ type: 'SET_PRODUCTS', payload: (products || []) as readonly Product[] });

      addNotification({
        type: 'success',
        title: 'Products Refreshed',
        message: 'Product list has been updated.',
        duration: 3000
      });

    } catch (error) {
      console.error('Error refreshing products:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh products';
      
      setError('products', errorMessage);
      addNotification({
        type: 'error',
        title: 'Refresh Failed',
        message: errorMessage,
        duration: 5000
      });
    } finally {
      setLoading('products', false);
    }
  }, [setLoading, setError, safeDispatch, supplierService, addNotification]);

  // Load dashboard data on mount
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Return memoized hook interface
  return useMemo(() => ({
    // State
    supplier: state.supplier,
    products: state.products,
    documents: state.documents,
    stats: state.stats,
    activeTab: state.activeTab,
    loadingStates: state.loadingStates,
    errorStates: state.errorStates,
    notifications: state.notifications,
    isInitialized: state.isInitialized,

    // Actions
    setActiveTab,
    loadDashboardData,
    deleteProduct,
    deleteDocument,
    uploadDocument,
    refreshProducts,
    clearErrors,
    addNotification,
    removeNotification,
    clearAllNotifications
  }), [
    state,
    setActiveTab,
    loadDashboardData,
    deleteProduct,
    deleteDocument,
    uploadDocument,
    refreshProducts,
    clearErrors,
    addNotification,
    removeNotification,
    clearAllNotifications
  ]);
}; 